import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ProjectTag, ProjectSelector } from "@ecologi/shared";
import { toggleArrayItem } from "../utils/toggle-array-item";
import ArrowSvg from "@ecologi/assets/icons/arrow-up.svg";
import { Typography } from "../Typography";
import { excludePropsFromForwarding } from "../utils";

const Button = styled.button<{ isSelected?: boolean }>`
  flex: 1 auto;
  min-height: 48px;
  padding: 1rem 2rem;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid
    ${({ isSelected, theme }) =>
      isSelected ? theme.colors.jungle : theme.colors.mist};
  background-color: ${({ theme }) => theme.colors.mist};

  &:hover {
    background-color: ${({ theme }) => theme.colors.mistDark};
    border-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.jungle : theme.colors.mistDark};
  }
`;

const AccordionButton = styled(Button)`
  flex: none;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border: 2px solid
    ${({ isSelected, theme }) =>
      isSelected ? theme.colors.jungle : theme.colors.mist};

  &:hover {
    border-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.jungle : theme.colors.mistDark};
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    width: auto;
    position: relative;
  }
`;

const ArrowGraphic = styled(ArrowSvg, excludePropsFromForwarding("isOpen"))<{
  isOpen: boolean;
}>`
  transform: rotate(0);
  transition: transform 0.2s ease-out;

  ${(props) =>
    !props.isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

interface FilterButtonProps {
  tag: ProjectTag;
  label: string;
  selectedTags: ProjectTag[];
  setSelectedTags: (tags: ProjectTag[]) => void;
}

export const FilterButton = ({
  tag,
  label,
  selectedTags,
  setSelectedTags,
}: FilterButtonProps) => {
  return (
    <Button
      isSelected={selectedTags.includes(tag)}
      onClick={() => {
        if (tag === ProjectSelector.All) {
          setSelectedTags([ProjectSelector.All]);
        } else {
          const tags = toggleArrayItem(selectedTags, tag);

          // 'All' should only exist on its own
          if (
            tags.length === 0 ||
            (tags.includes(ProjectSelector.All) && tags.length > 1)
          ) {
            setSelectedTags(toggleArrayItem(tags, ProjectSelector.All));
          } else {
            setSelectedTags(tags);
          }
        }
      }}
    >
      <Typography use="label" as="span">
        {label}
      </Typography>
    </Button>
  );
};

interface AccordionProps {
  isSelected: boolean;
  isOpen: boolean;
  onClick: () => void;
}

const Accordion = ({ isSelected, isOpen, onClick }: AccordionProps) => {
  return (
    <AccordionButton isSelected={isSelected} onClick={onClick}>
      <ArrowGraphic isOpen={isOpen} />
    </AccordionButton>
  );
};

FilterButton.Accordion = Accordion;
